import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStytchSession } from "@stytch/react";
import useAuthenticatedApiRequest from "../../AuthenticatedApiRequest";
import TchrURL from "../../TchrURL";
import type { ToolModel, ToolResponse } from "../../types/ToolModel";
import { processHtmlContent, processHtmlResults } from "../../utils/processHtmlContent";
import useUserSettings from "../user/useUserSettings";
import { useInvalidateUserStats } from "../user/useUserStats";
import * as Sentry from "@sentry/react";


export type ToolStageName =
  | "start"
  | "draft"
  | "format"
  | "translate"
  | "test"
  | "done"
  | (string & {}); // allow unknown future stages

export interface ToolStartEvent {
  timestamp: number;
  type: "start";
  body: null;
}

export interface ToolStageEvent {
  timestamp: number;
  type: "stage";
  body: { stage: ToolStageName };
}

export interface ToolProgressEvent {
  timestamp: number;
  type: "progress";
  body: { step: number; totalsteps: number; percent: number };
}

export interface ToolSingleResult {
  language: string;
  body: string; // often HTML
  type: string;
  filename: string | null;
  format: string; // e.g., "html"
}

export interface ToolOutputEvent {
  timestamp: number;
  type: "output";
  body: { result: ToolSingleResult };
}

export interface ToolCompleteEvent {
  timestamp: number;
  type: "complete";
  body: {
    results: ToolSingleResult[];
    references?: unknown;
    intermediate?: unknown;
  };
}

export interface ToolErrorEvent {
  timestamp: number;
  type: "error";
  body: { error: string; message?: string };
}

export type ToolSSEvent =
  | ToolStartEvent
  | ToolStageEvent
  | ToolProgressEvent
  | ToolOutputEvent
  | ToolCompleteEvent
  | ToolErrorEvent;

export type ProcessedResult = ToolSingleResult & { style: string; trackingIds: string[] };

export interface ToolStreamFinal extends Omit<ToolResponse, "results"> {
  results: ProcessedResult[];
}

export interface UseToolSubmissionSSEState {
  // stream state
  isStreaming: boolean;
  error: Error | null;
  // progressive info
  events: ToolSSEvent[];
  stage: ToolStageName | null;
  progress: { step: number; totalsteps: number; percent: number } | null;
  latestOutput: (ProcessedResult & { original: ToolSingleResult }) | null;
  // final
  final: ToolStreamFinal | null;
}

export interface UseToolSubmissionSSEApi extends UseToolSubmissionSSEState {
  start: (payload: ToolModel, opts?: { endpoint?: string }) => Promise<ToolStreamFinal | null>;
  abort: () => void;
  reset: () => void;
}

const parseStreamMessages = (buffer: string): { events: ToolSSEvent[]; rest: string } => {
  const events: ToolSSEvent[] = [];
  const normalized = buffer.replace(/\r\n/g, "\n");

  if (/\n\n/.test(normalized)) {
    const parts = normalized.split(/\n\n/);
    const completeParts = parts.slice(0, -1);
    const rest = parts[parts.length - 1] ?? "";

    for (const part of completeParts) {
      const lines = part.split(/\n/).map((l) => l.trim());
      const dataLines = lines.filter((l) => l.startsWith("data:"));
      let jsonText: string | null = null;

      if (dataLines.length) {
        jsonText = dataLines
          .map((l) => l.replace(/^data:\s?/, ""))
          .join("\n")
          .trim();
      } else {
        const trimmed = part.trim();
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) jsonText = trimmed;
      }

      if (!jsonText) continue;
      try {
        const obj = JSON.parse(jsonText);
        if (typeof obj?.timestamp === "number" && typeof obj?.type === "string") {
          events.push(obj as ToolSSEvent);
        }
      } catch {
        Sentry.captureException(new Error("Failed to parse SSE event"), { extra: { jsonText } });
      }
    }

    return { events, rest };
  }

  const lastNl = normalized.lastIndexOf("\n");
  if (lastNl === -1) {
    // No line break yet; keep buffering
    return { events: [], rest: normalized };
  }

  const completeBlock = normalized.slice(0, lastNl);
  const rest = normalized.slice(lastNl + 1);
  const lines = completeBlock.split("\n");
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let jsonText: string | null = null;
    if (trimmed.startsWith("data:")) {
      jsonText = trimmed.replace(/^data:\s?/, "");
    } else if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      jsonText = trimmed;
    }
    if (!jsonText) continue;
    try {
      const obj = JSON.parse(jsonText);
      if (typeof obj?.timestamp === "number" && typeof obj?.type === "string") {
        events.push(obj as ToolSSEvent);
      }
    } catch {
      Sentry.captureException(new Error("Failed to parse SSE event"), { extra: { jsonText } });
    }
  }
  return { events, rest };
};

const normalizeReferences = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;
  const refs = value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  return refs.length ? refs : null;
};

export const useToolSubmissionSSE = (endpoint: string): UseToolSubmissionSSEApi => {
  const { session, fromCache } = useStytchSession();
  const { post } = useAuthenticatedApiRequest();
  const queryClient = useQueryClient();
  const invalidateUserStats = useInvalidateUserStats();
  const { useGetUserSettings: useGetUser } = useUserSettings();
  const { data: userSettings } = useGetUser({ enabled: !!session && !fromCache });

  const [state, setState] = useState<UseToolSubmissionSSEState>({
    isStreaming: false,
    error: null,
    events: [],
    stage: null,
    progress: null,
    latestOutput: null,
    final: null,
  });

  const abortRef = useRef<AbortController | null>(null);
  const bufferRef = useRef<string>("");
  const mountedRef = useRef<boolean>(true);
  const seenCompleteRef = useRef<boolean>(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleTerminate = () => {
      try {
        abortRef.current?.abort();
      } catch {
        // no-op
      }
    };
    window.addEventListener("beforeunload", handleTerminate);
    return () => {
      window.removeEventListener("beforeunload", handleTerminate);
    };
  }, []);

  const buildTeacher = useMemo(() => {
    const teacherData = {
      name: `${userSettings?.firstName || ""} ${userSettings?.lastName || ""}`.trim() || "",
      school: userSettings?.school || "",
      language: userSettings?.resultLanguage
        ? userSettings.resultLanguage
        : userSettings?.displayLanguage || "english",
    };
    return teacherData;
  }, [userSettings?.firstName, userSettings?.lastName, userSettings?.school, userSettings?.displayLanguage, userSettings?.resultLanguage]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    bufferRef.current = "";
    seenCompleteRef.current = false;
    setState({
      isStreaming: false,
      error: null,
      events: [],
      stage: null,
      progress: null,
      latestOutput: null,
      final: null,
    });
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((s) => ({ ...s, isStreaming: false }));
  }, []);

  const updateCreditsFromHeaders = useCallback(
    (response: Response) => {
      if (response.status !== 200) return;
      queryClient.setQueryData(["user"], (oldData: any) => {
        const creditsRemainingResponse = response.headers.get("credits_remaining");
        const creditsMaxResponse = response.headers.get("credits_max");
        const creditsUsedResponse = response.headers.get("credits_used");
        if (!creditsRemainingResponse || !creditsMaxResponse || !creditsUsedResponse) return oldData;
        if (!oldData) return oldData;
        const creditsRemaining = parseInt(creditsRemainingResponse ?? "0", 10);
        const creditsMax = parseInt(creditsMaxResponse ?? "0", 10);
        const creditsUsed = parseInt(creditsUsedResponse ?? "0", 10);
        return { ...oldData, creditsRemaining, creditsMax, creditsUsed };
      });
    },
    [queryClient]
  );

  const start = useCallback<UseToolSubmissionSSEApi["start"]>(
    async (payload, opts) => {
      reset();
      if (mountedRef.current) setState((s) => ({ ...s, isStreaming: true }));

      const enhancedPayload: ToolModel = {
        ...payload,
        teacher: buildTeacher,
      };

      const effectiveEndpoint = opts?.endpoint ?? endpoint;
      const url = new TchrURL(`tools/${effectiveEndpoint}`);
      const logPrefix = `[ToolSSE:${effectiveEndpoint}]`;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await post(
          url,
          JSON.stringify(enhancedPayload),
          { enableClientKey: true, headersOpts: { Accept: "text/event-stream", events: "true", }, signal: controller.signal }
        );


        updateCreditsFromHeaders(response);

        const reader = response.body?.getReader();
        if (!reader) {
          console.error(`${logPrefix} error: response body is not a readable stream`);
          throw new Error("Streaming is not supported by this response");
        }

        const decoder = new TextDecoder();
        let finalResult: ToolStreamFinal | null = null;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          bufferRef.current += chunk;
          // Parse complete SSE messages from buffer
          const { events, rest } = parseStreamMessages(bufferRef.current);
          bufferRef.current = rest;


          if (!events.length) continue;

          if (!mountedRef.current) {
            for (const ev of events) {
              if (ev.type === "complete") {
                const body = (ev as ToolCompleteEvent)?.body ?? { results: [] };
                const arr = body?.results ?? [];
                const valid = (Array.isArray(arr) ? arr : []).filter(
                  (r: any): r is ToolSingleResult => r && typeof r.body === "string"
                );
                if (!valid.length) {
                  console.warn(`${logPrefix} complete event with no valid results`);
                }
                try {
                  const processedResults = valid.length
                    ? processHtmlResults<ToolSingleResult>(valid)
                    : [];
                  finalResult = {
                    intermediate: typeof body?.intermediate === "string" ? body.intermediate : null,
                    references: normalizeReferences(body?.references ?? null),
                    results: processedResults,
                  };
                } catch {
                  Sentry.captureException(new Error("Failed to process final html results"), { extra: { arr } });
                }
              }
            }
            continue;
          }

          setState((prev) => {
            let stage = prev.stage;
            let progress = prev.progress;
            let latestOutput = prev.latestOutput;
            let final = prev.final;
            let sawComplete = false;
            let stopNow = false;
            for (const ev of events) {
              switch (ev.type) {
                case "stage":
                  stage = ev.body.stage;
                  break;
                case "progress":
                  progress = ev.body;
                  break;
                case "output": {
                  const res = (ev as ToolOutputEvent)?.body?.result as ToolSingleResult | undefined;
                  if (!res || typeof res.body !== "string") {
                    // Sentry.captureException(new Error("Skipped output event without valid result.body"), { extra: { event: ev } });
                    break;
                  }
                  try {
                    const processed = processHtmlContent<ToolSingleResult>(res);
                    latestOutput = { ...processed, original: res };
                  } catch (e) {
                    Sentry.captureException(new Error("Failed to process output html"), { extra: { res, error: e } });
                  }
                  break;
                }
                case "complete": {
                  const body = (ev as ToolCompleteEvent)?.body ?? { results: [] };
                  const arr = body?.results ?? [];
                  const valid = (Array.isArray(arr) ? arr : []).filter(
                    (r: any): r is ToolSingleResult => r && typeof r.body === "string"
                  );
                  if (!valid.length) {
                    console.warn(`${logPrefix} complete event with no valid results`);
                  }
                  try {
                    const processedResults = valid.length
                      ? processHtmlResults<ToolSingleResult>(valid)
                      : [];
                    final = {
                      intermediate: typeof body?.intermediate === "string" ? body.intermediate : null,
                      references: normalizeReferences(body?.references ?? null),
                      results: processedResults,
                    };
                    finalResult = final; // also mirror to local so we can return it reliably
                    sawComplete = true;
                    seenCompleteRef.current = true;
                  } catch (e) {
                    console.warn(`${logPrefix} failed to process final html results`, e);
                  }
                  break;
                }
                case "error": {
                  const errBody = (ev as ToolErrorEvent).body;
                  const msg = errBody?.message || errBody?.error || "Stream error";
                  const e = new Error(msg);
                  Sentry.captureException(e, { extra: { endpoint: effectiveEndpoint, event: ev } });
                  stopNow = true;
                  return {
                    ...prev,
                    events: [...prev.events, ...events],
                    stage,
                    progress,
                    latestOutput,
                    final,
                    isStreaming: false,
                    error: e,
                  };
                }
                default:
                  break;
              }
            }

            return {
              ...prev,
              events: [...prev.events, ...events],
              stage,
              progress,
              latestOutput,
              final,
              isStreaming: stopNow || sawComplete ? false : prev.isStreaming,
              error: stopNow ? (prev.error || new Error("Stream error")) : prev.error,
            };
          });
        }

        invalidateUserStats();

        if (mountedRef.current) {
          setState((s) => ({
            ...s,
            // Only flip to false at loop end if we already saw a complete event.
            // This avoids premature false when multiple outputs stream progressively.
            isStreaming: seenCompleteRef.current ? false : s.isStreaming,
          }));
        }
        return finalResult;
      } catch (err: any) {
        if (controller.signal.aborted) {
          Sentry.captureMessage("Tool submission aborted by user", { level: "info", extra: { endpoint: effectiveEndpoint } });
          if (mountedRef.current) setState((s) => ({ ...s, isStreaming: false }));
          return null;
        }
        try {
          const e = err instanceof Error ? err : new Error(String(err));
          Sentry.captureException(e, { extra: { endpoint: effectiveEndpoint } });
          if (mountedRef.current) setState((s) => ({ ...s, isStreaming: false, error: e }));
        } catch {
          if (mountedRef.current)
            setState((s) => ({ ...s, isStreaming: false, error: err instanceof Error ? err : new Error(String(err)) }));
          Sentry.captureException(err instanceof Error ? err : new Error(String(err)), { extra: { endpoint: effectiveEndpoint } });
        }
        return null;
      } finally {
        abortRef.current = null;
      }
    },
    [endpoint, post, buildTeacher, invalidateUserStats, reset, updateCreditsFromHeaders, state.final]
  );

  return {
    ...state,
    start,
    abort,
    reset,
  };
};

export default useToolSubmissionSSE;
